import CharacterClass, {
  characterClasses,
} from 'app/frontend/types/custom/CharacterClass'
import {
  EventActivitiesPlanningInfo,
  RecommendedSetup,
} from 'app/frontend/base/api/event'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'

export type EventActivityRoleSetup = {
  tank: number
  heal: number
  melee: number
  ranged: number
}

type PlanningWishlistItemsByCharacter =
  EventActivitiesPlanningInfo['activities'][string]['wishlist_items']

export type LootWasteWantedBy = {
  characterId: number
  name: string
  characterClass: CharacterClass
}

export type LootWasteItem = {
  id: number
  icon: string
  name: string
  wantedBy: LootWasteWantedBy[]
}

export type EventActivityIssues = {
  missingClasses: CharacterClass[]
  actionableMissingClasses: CharacterClass[]
  recommendedMissingClasses: CharacterClass[]
  tooManyPlayers: boolean
  selectedCount: number
  raidSize: number | null
  issueCount: number
  recommendationCount: number
  hasRecommendedSetupMismatch: boolean
  recommendedSetup: RecommendedSetup | null
  recommendedRoleSetup: EventActivityRoleSetup | null
  selectedRoleSetup: EventActivityRoleSetup
  lootWasteItems: LootWasteItem[]
}

type ResolveEventActivityIssuesInput = {
  activity: wowaudit.EventActivity
  entries: EventActivityParticipantEntry[]
  unavailableStatuses: wowaudit.EventParticipantReference['status'][]
  recommendedSetup?: RecommendedSetup | null
  planningWishlistItemsByCharacter?: PlanningWishlistItemsByCharacter
}

const minimumRecommendedSetupFraction = 0.1
const resolveRoleSetupFraction = (
  distribution: RecommendedSetup['tanks']['distribution'],
  amount: number,
) => distribution.find((entry) => entry.amount === amount)?.fraction ?? 0

export const resolveEventActivityIssues = ({
  activity,
  entries,
  unavailableStatuses,
  recommendedSetup,
  planningWishlistItemsByCharacter,
}: ResolveEventActivityIssuesInput): EventActivityIssues => {
  const selectedEntries = entries.filter((entry) => {
    if (unavailableStatuses.includes(entry.participant.status)) return false
    return entry.selections[activity.id]?.selected
  })
  const availableEntries = entries.filter(
    (entry) => !unavailableStatuses.includes(entry.participant.status),
  )
  const selectedCount = selectedEntries.length
  const availableRepresentedClasses = new Set<CharacterClass>(
    availableEntries
      .map((entry) => resolveEventParticipantCharacter(entry, activity.id))
      .map((character) => character.characterClass as CharacterClass),
  )
  const selectedClasses = new Set<CharacterClass>(
    selectedEntries
      .map((entry) => resolveEventParticipantCharacter(entry, activity.id))
      .map((character) => character.characterClass as CharacterClass),
  )
  const missingClasses = characterClasses.filter(
    (characterClass) => !selectedClasses.has(characterClass),
  )
  const actionableMissingClasses = missingClasses.filter((characterClass) =>
    availableRepresentedClasses.has(characterClass),
  )
  const recommendedMissingClasses = missingClasses.filter(
    (characterClass) => !availableRepresentedClasses.has(characterClass),
  )
  const selectedCharacterIds = new Set<number>(
    selectedEntries.map(
      (entry) => resolveEventParticipantCharacter(entry, activity.id).id,
    ),
  )
  const availableCharactersById = new Map<number, LootWasteWantedBy>()
  availableEntries.forEach((entry) => {
    const character = resolveEventParticipantCharacter(entry, activity.id)
    availableCharactersById.set(character.id, {
      characterId: character.id,
      name: character.name,
      characterClass: character.characterClass as CharacterClass,
    })
  })
  const benchedCharactersById = new Map<number, LootWasteWantedBy>(
    Array.from(availableCharactersById.entries()).filter(
      ([characterId]) => !selectedCharacterIds.has(characterId),
    ),
  )
  const lootWasteItems = Object.values(
    Object.values(planningWishlistItemsByCharacter ?? {}).reduce<
      Record<
        number,
        {
          id: number
          icon: string
          name: string
          wantedByCharacterIds: Set<number>
        }
      >
    >((byId, wishlistItems) => {
      wishlistItems.forEach((wishlistItem) => {
        const existing = byId[wishlistItem.id]
        if (existing) return
        byId[wishlistItem.id] = {
          id: wishlistItem.id,
          icon: wishlistItem.icon,
          name: wishlistItem.name,
          wantedByCharacterIds: new Set<number>(),
        }
      })
      return byId
    }, {}),
  )
    .map((item) => {
      Object.entries(planningWishlistItemsByCharacter ?? {}).forEach(
        ([characterId, wishlistItems]) => {
          if (
            !wishlistItems.some((wishlistItem) => wishlistItem.id === item.id)
          ) {
            return
          }
          item.wantedByCharacterIds.add(Number(characterId))
        },
      )
      const wantedBy = Array.from(item.wantedByCharacterIds)
        .filter((characterId) => benchedCharactersById.has(characterId))
        .map((characterId) => benchedCharactersById.get(characterId))
        .filter((character): character is LootWasteWantedBy => !!character)
        .sort((left, right) => left.name.localeCompare(right.name))
      const hasSelectedWanter = Array.from(item.wantedByCharacterIds).some(
        (characterId) => selectedCharacterIds.has(characterId),
      )
      if (wantedBy.length === 0 || hasSelectedWanter) return null
      return {
        id: item.id,
        icon: item.icon,
        name: item.name,
        wantedBy,
      }
    })
    .filter((item): item is LootWasteItem => !!item)
  const raidSize = activity.raidSize ?? null
  const tooManyPlayers = raidSize !== null ? selectedCount > raidSize : false
  const selectedRoleSetup = selectedEntries.reduce<EventActivityRoleSetup>(
    (acc, entry) => {
      const role = entry.selections[activity.id]?.role
      if (role === 'tank') acc.tank += 1
      if (role === 'heal') acc.heal += 1
      if (role === 'melee') acc.melee += 1
      if (role === 'ranged') acc.ranged += 1
      return acc
    },
    {
      tank: 0,
      heal: 0,
      melee: 0,
      ranged: 0,
    },
  )
  const recommendedRoleSetup = recommendedSetup
    ? {
        tank: recommendedSetup.tanks.recommendation,
        heal: recommendedSetup.healers.recommendation,
        melee: recommendedSetup.melee.recommendation,
        ranged: recommendedSetup.ranged.recommendation,
      }
    : null
  const hasRecommendedSetupMismatch = recommendedSetup
    ? resolveRoleSetupFraction(
        recommendedSetup.tanks.distribution,
        selectedRoleSetup.tank,
      ) < minimumRecommendedSetupFraction ||
      resolveRoleSetupFraction(
        recommendedSetup.healers.distribution,
        selectedRoleSetup.heal,
      ) < minimumRecommendedSetupFraction ||
      resolveRoleSetupFraction(
        recommendedSetup.melee.distribution,
        selectedRoleSetup.melee,
      ) < minimumRecommendedSetupFraction ||
      resolveRoleSetupFraction(
        recommendedSetup.ranged.distribution,
        selectedRoleSetup.ranged,
      ) < minimumRecommendedSetupFraction
    : false

  return {
    missingClasses,
    actionableMissingClasses,
    recommendedMissingClasses,
    tooManyPlayers,
    selectedCount,
    raidSize,
    hasRecommendedSetupMismatch,
    recommendedSetup: recommendedSetup ?? null,
    recommendedRoleSetup,
    selectedRoleSetup,
    lootWasteItems,
    issueCount:
      (actionableMissingClasses.length > 0 ? 1 : 0) + (tooManyPlayers ? 1 : 0),
    recommendationCount:
      (recommendedMissingClasses.length > 0 ? 1 : 0) +
      (hasRecommendedSetupMismatch ? 1 : 0) +
      lootWasteItems.length,
  }
}
